import { fetchStudentCourseProfile, signOffStudent } from "../../api/apiClient";

/**
 * Reads a failed sign-off. A 409 with a `missing` list means the server refused because the student has not met the
 * class requirements: { requirementsNotMet: true, missing: [{code,label}], message }.
 */
export function readSignOffError(err) {
  const data = err?.response?.data || {};
  const missing = Array.isArray(data.missing) ? data.missing : [];
  return {
    status: err?.response?.status,
    requirementsNotMet: err?.response?.status === 409 && missing.length > 0,
    missing,
    message: data.error || data.message || "Sign-off failed",
  };
}

/**
 * Signs off each student as passed, one at a time. The server decides who is eligible (shared requirements rule):
 * a refused student (409 + missing list) is SKIPPED with the server's reasons. A student already signed off is
 * signed off again, which moves their sign-off date to today (e.g. after a session was added past the old date).
 *
 * @param {string|number} classId
 * @param {{ userId: string|number, name: string }[]} students
 * @returns {Promise<{ userId, name, outcome: "SIGNED_OFF"|"RESIGNED"|"SKIPPED"|"FAILED", reason?: string }[]>}
 */
export async function bulkSignOff(classId, students) {
  const results = [];
  for (const student of students) {
    results.push(await signOffOne(classId, student));
  }
  return results;
}

async function signOffOne(classId, { userId, name }) {
  // Only to tell "signed off" from "signed off again"; the server does the eligibility check.
  const alreadyPassed = await fetchStudentCourseProfile(classId, userId).then((p) => Boolean(p?.passed), () => false);
  try {
    await signOffStudent(classId, userId, true);
    return { userId, name, outcome: alreadyPassed ? "RESIGNED" : "SIGNED_OFF" };
  } catch (err) {
    const e = readSignOffError(err);
    if (e.requirementsNotMet) {
      return { userId, name, outcome: "SKIPPED", reason: `Not met: ${e.missing.map((m) => m.label).join("; ")}` };
    }
    return { userId, name, outcome: "FAILED", reason: e.message };
  }
}
